import { useState, useEffect } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { getJugadoresByLetrasJugador } from "../services/jugadorEstadisticasService";
import { getEquiposPorIdTemporadaYLetras } from "../services/equiposService";
import { getJugadoresPorIdEquipo } from "../services/equiposService";

const BuscarJugadorOEquipo = ({ onJugadorSeleccionado }) => {
  const [, setNombre] = useState("");
  const [modoBusqueda, setModoBusqueda] = useState("jugador"); // "jugador" o "equipo"
  const [textoBusqueda, setTextoBusqueda] = useState("");
  const [jugadores, setJugadores] = useState([]);
  const [jugadorSeleccionado, setJugadorSeleccionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [debouncedNombre, setDebouncedNombre] = useState("");
  const [equipoSeleccionado, setEquipoSeleccionado] = useState(null);
  const [equipos, setEquipos] = useState([]);
  const [listaEquiposVisible, setListaEquiposVisible] = useState(false);
  const [listaJugadoresVisible, setListaJugadoresVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const textoNormalizado = textoBusqueda.trim();

      if (textoNormalizado.length >= 3) {
        setDebouncedNombre(textoNormalizado);
      } else {
        setDebouncedNombre("");
        setJugadores([]);
        if (modoBusqueda === "equipo") setEquipos([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [textoBusqueda, modoBusqueda]);

  // 🔎 Buscar jugadores cuando debouncedNombre cambia
  useEffect(() => {
    if (modoBusqueda === "jugador") {
      if (!debouncedNombre) return;
      if (debouncedNombre === jugadorSeleccionado?.nombre) return; // Evitar hacer la consulta si ya está seleccionado
      setLoading(true);
      getJugadoresByLetrasJugador(debouncedNombre)
        .then((response) => {
          const jugadoresUnicos = Object.values(
            response.reduce((acc, jugador) => {
              acc[jugador.idJugador] = jugador;
              return acc;
            }, {})
          );
          setJugadores(
            jugadoresUnicos.sort((a, b) => a.nombre.localeCompare(b.nombre))
          );
        })
        .catch((error) => { /* silently handled */ })
        .finally(() => setLoading(false));
    } else {
      if (!debouncedNombre) return;
      if (debouncedNombre === equipoSeleccionado?.nombre) return; // Evitar hacer la consulta si ya está seleccionado
      setLoading(true);
      getEquiposPorIdTemporadaYLetras(20, debouncedNombre)
        .then((response) => {
          const equiposUnicos = Object.values(
            response.reduce((acc, equipo) => {
              acc[equipo.id] = {
                ...equipo,
                nombreCompleto: `${equipo.nombre} - ${
                  equipo.competicion?.nombreCategoria || "Sin categoría"
                }`,
              };
              return acc;
            }, {})
          );
          setEquipos(equiposUnicos.sort((a, b) => a.orden - b.orden));
          setListaEquiposVisible(true);
        })
        .catch((error) => { /* silently handled */ })
        .finally(() => setLoading(false));
    }
  }, [
    debouncedNombre,
    modoBusqueda,
    jugadorSeleccionado?.nombre,
    equipoSeleccionado?.nombre,
  ]);

  // 🏆 Seleccionar un jugador y hacer otra consulta
  const handleSeleccionJugador = (jugador) => {
    setNombre(jugador.nombre);
    setJugadores([]); // Oculta la lista tras seleccionar
    setJugadorSeleccionado(jugador);
    // Llamar a la función de la prop para hacer otra consulta
    onJugadorSeleccionado(jugador);
  };

  const handleSeleccionEquipo = (equipo) => {
    setJugadores([]); // Oculta la lista tras seleccionar
    setEquipoSeleccionado(equipo);
    setListaEquiposVisible(false);

    getJugadoresPorIdEquipo(20, equipo.id)
      .then((response) => {
        const jugadoresUnicos = Object.values(
          response.reduce((acc, jugador) => {
            acc[jugador.idJugador] = jugador;
            return acc;
          }, {})
        );
        setJugadores(
          jugadoresUnicos.sort((a, b) => a.nombre.localeCompare(b.nombre))
        );
      })
      .catch((error) => { /* silently handled */ })
      .finally(() => setLoading(false));
    setListaJugadoresVisible(true);
    setTextoBusqueda("");
    setDebouncedNombre("");
    // Llamar a la función de la prop para hacer otra consulta
  };

  return (
    <div>
      <Select
        value={modoBusqueda}
        onChange={(e) => setModoBusqueda(e.target.value)}
        sx={{ mb: 2 }}
      >
        <MenuItem value="jugador">Buscar por Jugador</MenuItem>
        <MenuItem value="equipo">Buscar por Equipo</MenuItem>
      </Select>

      <TextField
        label={modoBusqueda === "jugador" ? "Buscar jugador" : "Buscar equipo"}
        variant="outlined"
        fullWidth
        value={textoBusqueda}
        onChange={(e) => setTextoBusqueda(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {loading ? <CircularProgress size={24} /> : null}
              {textoBusqueda && (
                <IconButton onClick={() => setTextoBusqueda("")} edge="end">
                  <ClearIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />

      {loading && <CircularProgress size={24} style={{ marginTop: 10 }} />}

      {modoBusqueda === "jugador" && !loading && jugadores?.length > 0 && (
        <List>
          {jugadores?.map((jugador) => (
            <ListItem
              key={jugador?.id}
              onClick={() => handleSeleccionJugador(jugador)}
              sx={{
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
            >
              <ListItemText primary={jugador.nombre} />
            </ListItem>
          ))}
        </List>
      )}
      {modoBusqueda === "equipo" &&
        !loading &&
        equipos?.length > 0 &&
        listaEquiposVisible && (
          <List>
            {equipos?.map((equipo) => (
              <ListItem
                key={equipo?.id}
                onClick={() => handleSeleccionEquipo(equipo)}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#f0f0f0" },
                }}
              >
                <ListItemText primary={equipo.nombreCompleto} />
              </ListItem>
            ))}
          </List>
        )}
      {modoBusqueda === "equipo" &&
        !loading &&
        jugadores?.length > 0 &&
        listaJugadoresVisible && (
          <List>
            {jugadores?.map((jugador) => (
              <ListItem
                key={jugador?.id}
                onClick={() => handleSeleccionJugador(jugador)}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#f0f0f0" },
                }}
              >
                <ListItemText primary={jugador.nombre} />
              </ListItem>
            ))}
          </List>
        )}
    </div>
  );
};
export default BuscarJugadorOEquipo;
